import { useEffect, useRef, useState } from "react";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { Emojiful } from "../../components/lux/Atmosphere";
import { DEFAULT_LETTER, DEFAULT_TEASER, DEFAULT_HEADING } from "@/shared/birthday";
function AdminContent() {
  const settings = trpc.admin.getSettings.useQuery();
  const save = trpc.admin.saveSettings.useMutation();
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState(null);
  const [dirty, setDirty] = useState(false);
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (!settings.data || loadedOnce.current) return;
    loadedOnce.current = true;
    setDraft({
      herNickname: settings.data.herNickname,
      myNickname: settings.data.myNickname,
      mainHeading: settings.data.mainHeading,
      teaserText: settings.data.teaserText,
      letterBody: settings.data.letterBody,
      finalMessage: settings.data.finalMessage,
      subtitles: settings.data.subtitles ?? []
    });
  }, [settings.data]);
  const update = (key, value) => {
    setDraft((d) => d ? { ...d, [key]: value } : d);
    setDirty(true);
    setStatus(null);
  };
  const onSave = async () => {
    if (!draft) return;
    setStatus(null);
    try {
      await save.mutateAsync({
        herNickname: draft.herNickname.trim() || "Baby",
        myNickname: draft.myNickname.trim(),
        mainHeading: draft.mainHeading.trim() || DEFAULT_HEADING,
        teaserText: draft.teaserText.trim() || DEFAULT_TEASER,
        letterBody: draft.letterBody,
        finalMessage: draft.finalMessage,
        subtitles: draft.subtitles.filter((s) => s.trim()).map((s) => s.trim())
      });
      setDirty(false);
      await utils.admin.getSettings.invalidate();
      await utils.public.state.invalidate();
      setStatus({ kind: "ok", msg: "Saved to the site. Visitors see it on their next load." });
    } catch (e) {
      setStatus({ kind: "err", msg: e instanceof Error ? e.message : "Save failed." });
    }
  };
  const resetLetter = () => {
    update("letterBody", DEFAULT_LETTER);
  };
  if (settings.isLoading || !draft) {
    return <Panel eyebrow="Words" title="Loading…">
        <div />
      </Panel>;
  }
  return <Panel
    eyebrow="Words"
    title="Everything she reads"
    description="The letter supports plain text with paragraph breaks — the blank line between paragraphs is preserved exactly as you type it."
    aside={<div className="flex items-center gap-2">
          {dirty && <span className="pill">Unsaved changes</span>}
          <button type="button" onClick={onSave} className="btn-gold" disabled={save.isPending}>
            {save.isPending ? "Saving\u2026" : "Save words"}
          </button>
        </div>}
  >
      {status && <div
    role="status"
    className={`mb-5 rounded-xl border px-4 py-3 text-sm ${status.kind === "ok" ? "border-[rgba(126,217,154,.35)] bg-[rgba(38,120,66,.14)] text-[#a8e6bd]" : "border-[rgba(244,63,94,.4)] bg-[rgba(244,63,94,.14)] text-[#ffc9cd]"}`}
  >
          {status.msg}
        </div>}

      <div className="grid gap-5 lg:grid-cols-[1fr_400px]">
        {
    /* ── form ── */
  }
        <div className="space-y-4">
          <div className="glass rounded-xl p-5">
            <p className="eyebrow mb-4">Names</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Her nickname" hint="Used in the heading and the letter">
                <input
    className="field-lux"
    value={draft.herNickname}
    onChange={(e) => update("herNickname", e.target.value)}
    placeholder="Baby"
  />
              </Field>
              <Field label="Your name / nickname" hint="Signed at the end of the letter">
                <input
    className="field-lux"
    value={draft.myNickname}
    onChange={(e) => update("myNickname", e.target.value)}
    placeholder="Me"
  />
              </Field>
            </div>
          </div>

          <div className="glass rounded-xl p-5">
            <p className="eyebrow mb-4">Headings</p>
            <div className="space-y-4">
              <Field label="Teaser line" hint="Shown before midnight, above the countdown">
                <textarea
    className="field-lux !min-h-[76px]"
    value={draft.teaserText}
    onChange={(e) => update("teaserText", e.target.value)}
  />
              </Field>
              <Field label="Main heading" hint="The big reveal headline">
                <input
    className="field-lux"
    value={draft.mainHeading}
    onChange={(e) => update("mainHeading", e.target.value)}
  />
              </Field>
              <Field
    label="Subtitles"
    hint="Small lines under the heading. The first one also appears at the very top of the reveal."
  >
                <div className="space-y-2">
                  {draft.subtitles.map((s, i) => <div key={i} className="flex gap-2">
                      <input
    className="field-lux"
    value={s}
    onChange={(e) => {
      const next = [...draft.subtitles];
      next[i] = e.target.value;
      update("subtitles", next);
    }}
    placeholder={i === 0 ? "Today is yours" : "Optional extra line"}
  />
                      <button
    type="button"
    onClick={() => update("subtitles", draft.subtitles.filter((_, j) => j !== i))}
    className="shrink-0 rounded-lg border border-[rgba(233,168,242,.2)] px-3 text-xs text-[color:var(--muted-foreground)] hover:text-[#ffc9cd]"
    aria-label={`Remove subtitle ${i + 1}`}
  >
                        ✕
                      </button>
                    </div>)}
                  {draft.subtitles.length < 6 && <button
    type="button"
    onClick={() => update("subtitles", [...draft.subtitles, ""])}
    className="text-xs uppercase tracking-[0.16em] text-[#e9a8f2] hover:underline"
  >
                      + Add a subtitle
                    </button>}
                </div>
              </Field>
            </div>
          </div>

          <div className="glass rounded-xl p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="eyebrow">The love letter</p>
              <button
    type="button"
    onClick={resetLetter}
    className="text-[0.68rem] uppercase tracking-[0.14em] text-[color:var(--muted-foreground)] hover:text-[#e9a8f2]"
  >
                Restore default letter
              </button>
            </div>
            <textarea
    className="field-lux !min-h-[320px] font-[var(--font-display)] !text-[1.02rem] !leading-[1.8]"
    value={draft.letterBody}
    onChange={(e) => update("letterBody", e.target.value)}
    placeholder={DEFAULT_LETTER}
    aria-label="The love letter"
  />
            <p className="mt-2 text-[0.7rem] text-[color:var(--muted-foreground)]">
              {draft.letterBody.trim().split(/\s+/).filter(Boolean).length} words ·{" "}
              {draft.letterBody.split(/\n\s*\n/).filter(Boolean).length} paragraphs · typed out one
              character at a time on the reveal screen
            </p>
          </div>

          <div className="glass rounded-xl p-5">
            <p className="eyebrow mb-4">Final message</p>
            <textarea
    className="field-lux !min-h-[110px]"
    value={draft.finalMessage}
    onChange={(e) => update("finalMessage", e.target.value)}
    placeholder="The last thing she reads, under the animated heart."
    aria-label="Final message"
  />
          </div>
        </div>

        {
    /* ── live preview ── */
  }
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="glass gold-frame overflow-hidden rounded-xl">
            <div className="flex items-center justify-between border-b border-[rgba(233,168,242,.14)] px-4 py-2.5">
              <p className="eyebrow">Live preview</p>
              <span className="text-[0.62rem] uppercase tracking-[0.14em] text-[color:var(--muted-foreground)]">
                draft — not saved
              </span>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-5 py-6">
              <p className="eyebrow">Before midnight</p>
              <p className="display-md mt-2 gold-text"><Emojiful text={draft.teaserText || DEFAULT_TEASER} /></p>

              <div className="gold-rule my-6" />

              <p className="eyebrow">Reveal</p>
              <p className="display-md mt-2 gold-text"><Emojiful text={draft.mainHeading || DEFAULT_HEADING} /></p>
              {draft.subtitles.filter(Boolean).length > 0 && <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {draft.subtitles.filter(Boolean).map((s, i) => <span key={i} className="text-[0.6rem] uppercase tracking-[0.2em] text-[color:var(--muted-foreground)]">
                      {s}
                    </span>)}
                </div>}

              <div className="mt-5 rounded-lg border border-[rgba(233,168,242,.14)] bg-black/25 p-4">
                <p className="prose-romance whitespace-pre-line text-[0.9rem]">
                  {draft.letterBody || DEFAULT_LETTER}
                </p>
                <p className="mt-4 text-right font-display text-sm italic text-[color:var(--gold-dim)]">
                  — {draft.myNickname || "Me"}
                </p>
              </div>

              {draft.finalMessage && <>
                  <div className="gold-rule my-6" />
                  <p className="eyebrow">Final message</p>
                  <p className="prose-romance mt-2 text-[0.88rem] whitespace-pre-line">
                    {draft.finalMessage}
                  </p>
                </>}
            </div>
          </div>
        </div>
      </div>

      <Messages />
    </Panel>;
}
function Field({
  label,
  hint,
  children
}) {
  return <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[#c9cce0]">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-[0.68rem] text-[color:var(--muted-foreground)]">{hint}</span>}
    </label>;
}
function Messages() {
  const list = trpc.admin.listMessages.useQuery();
  const create = trpc.admin.createMessage.useMutation();
  const update = trpc.admin.updateMessage.useMutation();
  const remove = trpc.admin.deleteMessage.useMutation();
  const utils = trpc.useUtils();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("hidden");
  const [revealAt, setRevealAt] = useState("");
  const [err, setErr] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const refresh = async () => {
    await utils.admin.listMessages.invalidate();
    await utils.public.state.invalidate();
  };
  const add = async () => {
    setErr(null);
    if (!title.trim() || !body.trim()) {
      setErr("A message needs both a title and a body.");
      return;
    }
    try {
      await create.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        kind,
        // `datetime-local` has no timezone; interpret it as the browser's local
        // time (what the owner means when they pick a moment) and send an ISO
        // string so the server stores an unambiguous instant.
        revealAt: kind === "scheduled" && revealAt ? new Date(revealAt).toISOString() : null
      });
      setTitle("");
      setBody("");
      setRevealAt("");
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not add that message.");
    }
  };
  const rows = list.data ?? [];
  return <div className="mt-10">
      <p className="eyebrow">Messages with timing</p>
      <h3 className="display-md mt-2 text-[#f3f4f9]">Notes, gifts and hidden words</h3>
      <p className="mt-2 max-w-2xl text-sm text-[color:var(--muted-foreground)]">
        <strong className="text-[#e9a8f2]">Scheduled</strong> notes unlock at their own time (and are
        hidden before it). <strong className="text-[#e9a8f2]">Hidden</strong> and{" "}
        <strong className="text-[#e9a8f2]">gift</strong> notes only exist after midnight, behind a tap.
      </p>

      <div className="glass mt-5 rounded-xl p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <input
    className="field-lux"
    placeholder="Title — e.g. Read this when you miss me"
    value={title}
    onChange={(e) => setTitle(e.target.value)}
    aria-label="Message title"
  />
          <div className="flex gap-2">
            <select
    className="field-lux"
    value={kind}
    onChange={(e) => setKind(e.target.value)}
    aria-label="Message type"
  >
              <option value="hidden">Hidden note</option>
              <option value="gift">Gift box</option>
              <option value="scheduled">Scheduled</option>
            </select>
            {kind === "scheduled" && <input
    type="datetime-local"
    className="field-lux"
    value={revealAt}
    onChange={(e) => setRevealAt(e.target.value)}
    aria-label="Reveal at"
  />}
          </div>
        </div>
        <textarea
    className="field-lux mt-3 !min-h-[90px]"
    placeholder="The message itself…"
    value={body}
    onChange={(e) => setBody(e.target.value)}
    aria-label="Message body"
  />
        <div className="mt-3 flex items-center gap-3">
          <button type="button" onClick={add} className="btn-gold !min-h-[40px] !text-[0.66rem]" disabled={create.isPending}>
            {create.isPending ? "Adding\u2026" : "Add message"}
          </button>
          {kind === "scheduled" && !revealAt && <span className="text-[0.7rem] text-[#f3c2ce]">
              Pick a time, or this note will stay hidden until you set one.
            </span>}
        </div>
        {err && <p className="mt-2 text-sm text-[#ffc9cd]">{err}</p>}
      </div>

      <div className="mt-4 space-y-3">
        {rows.length === 0 && <div className="glass rounded-xl px-6 py-10 text-center">
            <p className="font-display text-lg text-[#f3f4f9]">No messages yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-[color:var(--muted-foreground)]">
              Add a gift box or a hidden note above. They appear on her page after midnight, each
              opening when she taps it.
            </p>
          </div>}

        {rows.map((m) => <div key={m.id} className="glass rounded-xl p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`pill ${m.kind === "scheduled" ? "pill-locked" : ""}`}>{m.kind}</span>
                  {m.revealAt && <span className="text-[0.68rem] text-[color:var(--muted-foreground)]">
                      {new Date(m.revealAt).toLocaleString()}
                    </span>}
                  {!m.isVisible && <span className="pill pill-locked">hidden from site</span>}
                </div>
                <input
    className="field-lux mt-2 !min-h-[38px] !text-sm"
    defaultValue={m.title}
    onBlur={(e) => {
      if (e.target.value.trim() && e.target.value !== m.title) {
        void update.mutateAsync({ id: m.id, title: e.target.value.trim() }).then(refresh);
      }
    }}
    aria-label="Message title"
  />
                <textarea
    className="field-lux mt-2 !min-h-[70px] !text-sm"
    defaultValue={m.body}
    onBlur={(e) => {
      if (e.target.value.trim() && e.target.value !== m.body) {
        void update.mutateAsync({ id: m.id, body: e.target.value.trim() }).then(refresh);
      }
    }}
    aria-label="Message body"
  />
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <button
    type="button"
    onClick={() => void update.mutateAsync({ id: m.id, isVisible: !m.isVisible }).then(refresh)}
    className="btn-ghost-lux !min-h-[34px] !px-3 !text-[0.62rem]"
  >
                  {m.isVisible ? "Hide" : "Show"}
                </button>
                {confirmId === m.id ? <div className="flex gap-1">
                    <button
    type="button"
    onClick={async () => {
      await remove.mutateAsync({ id: m.id });
      setConfirmId(null);
      await refresh();
    }}
    className="rounded-full bg-[#e11d48] px-3 py-1.5 text-[0.62rem] uppercase tracking-wider text-white"
  >
                      Delete
                    </button>
                    <button
    type="button"
    onClick={() => setConfirmId(null)}
    className="rounded-full border border-white/15 px-3 py-1.5 text-[0.62rem] uppercase tracking-wider text-[color:var(--muted-foreground)]"
  >
                      No
                    </button>
                  </div> : <button
    type="button"
    onClick={() => setConfirmId(m.id)}
    className="btn-ghost-lux !min-h-[34px] !px-3 !text-[0.62rem]"
  >
                    Delete
                  </button>}
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}
export {
  AdminContent as default
};
